import { useGetBriefDeliverable } from "@entities/brief/lib/hooks/useGetBriefDeliverable";
import { useSetDefaultDeliverable } from "@entities/brief/lib/hooks/useSetDefaultDeliverable";
import { PackageIcon, StarFourIcon } from "@phosphor-icons/react";
import { ToastService } from "@shared/lib/services";
import { IBriefDeliverable, IBriefDeliverableResponse } from "@shared/lib/types/briefInfluencerV2";
import { Button } from "@shared/ui/Button";
import { DeliverablePillSummary } from "@shared/ui/DeliverablePillSummary";
import {
  Avatar,
  Card,
  Divider,
  Flex,
  Modal,
  Tag,
  Tooltip,
  Typography
} from "antd";
import { Copy, PenLine, Zap } from "lucide-react";
import { MouseEvent, useEffect, useState } from "react";
import styles from "./styles.module.scss";

const colorPalette = [
  "#ffadad",
  "#ffd6a5",
  "#fdffb6",
  "#caffbf",
  "#9bf6ff",
  "#a0c4ff",
  "#bdb2ff",
  "#ffc6ff",
];

const getColorFromId = (id: string) => {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colorPalette[Math.abs(hash) % colorPalette.length];
};

export default function DeliverablePackageCard({
  defaultDeliverableId,
  deliverableId,
  onClose,
  isEditable,
  briefInfluencerIds,
  briefId,
  deliverableInfo,
  showEditIcon,
  editFn,
  showCopyIcon,
  copyFn,
  hideOldWarning = false,
  showMarkDefaultButton,
}: {
  defaultDeliverableId?: string;
  deliverableId?: string;
  onClose?: () => void;
  isEditable?: boolean;
  briefInfluencerIds?: string[];
  briefId: string;
  deliverableInfo?: IBriefDeliverableResponse | IBriefDeliverable;
  showEditIcon?: boolean;
  editFn?: () => void;
  showCopyIcon?: boolean;
  copyFn?: () => void;
  hideOldWarning?: boolean;
  showMarkDefaultButton?: boolean;
}) {
  const { deliverable, isFetching } = useGetBriefDeliverable(
    deliverableId || ""
  );

  // Normalize deliverableInfo — it can be either the full response shape
  // (IBriefDeliverableResponse with a nested `deliverableV2`) or a flat
  // IBriefDeliverable object passed directly.
  const isResponseShape = (info: IBriefDeliverableResponse | IBriefDeliverable | undefined): info is IBriefDeliverableResponse =>
    info != null && "deliverableV2" in info;

  const normalizedFromProp: IBriefDeliverableResponse | undefined = deliverableInfo
    ? isResponseShape(deliverableInfo)
      ? deliverableInfo
      : { deliverableV2: deliverableInfo as IBriefDeliverable, countUsed: 0, usedByBriefInfluencers: [] }
    : undefined;

  const deliverableData = deliverable || normalizedFromProp;
  // const [openPackageList, setOpenPackageList] = useState<boolean>(false);
  const [showEditConfirmModal, setShowEditConfirmModal] =
    useState<boolean>(false);
  const [selectedDeliverableForAttach, setSelectedDeliverableForAttach] =
    useState<{ _id: string } | null>(null);
  const [cardHovered, setCardHovered] = useState(false);

  const countUsedByBriefInfluencersWithPurchased =
    deliverableData?.countUsedByBriefInfluencersWithPurchased || 0;
  // Only show assigned-to section when the response actually carries this info
  const hasUsageInfo =
    isResponseShape(deliverableInfo) || !!deliverable;
  const countUsed = deliverableData?.countUsed || 0;

  const { setDefaultDeliverable, isSet, isSetError, isSetting } =
    useSetDefaultDeliverable(briefId);

  // const {
  //   attachBriefDeliverable,
  //   isAttaching,
  //   isAttached,
  //   attachBriefDeliverableError,
  // } = useAttachBriefDeliverable(briefId);

  useEffect(() => {
    if (isSet) {
      ToastService.success("Default deliverable added successfully.");
    } else if (isSetError) {
      ToastService.error("Default deliverable couldn't be added.");
    }
  }, [isSet, isSetError]);

  // useEffect(() => {
  //   if (isAttached) {
  //     ToastService.success("Deliverable attached successfully.");
  //     setOpenPackageList(false);
  //     setSelectedDeliverableForAttach(null);
  //     onClose?.();
  //   } else if (attachBriefDeliverableError) {
  //     ToastService.error(attachBriefDeliverableError?.message);
  //   }
  // }, [isAttached, attachBriefDeliverableError, onClose]);

  const handleEditClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    if (countUsedByBriefInfluencersWithPurchased > 0) {
      setShowEditConfirmModal(true);
      return;
    }

    if (countUsed > 0) {
      Modal.confirm({
        title: "Edit Deliverable Package",
        content:
          "This deliverable is sent to creators. Editing will change it for all creators. Do you want to continue?",
        onOk: () => {
          editFn?.();
        },
      });
      return;
    }

    editFn?.();
  };

  return (
    <Flex vertical gap={20} style={{ width: "100%" }}>
      <Card
        loading={isFetching}
        bordered
        onMouseEnter={() => setCardHovered(true)}
        onMouseLeave={() => setCardHovered(false)}
        className={cardHovered ? styles.packageCardHovered : styles.packageCard}
        styles={{
          header: {
            padding: 10,
            minHeight: 0,
            borderBottomColor: "#f6f9fc",
          },
          body: {
            padding: 10,
            display: "flex",
            flexDirection: "column",
          },
        }}
        title={
          isFetching ? null : (
            <Flex justify="space-between" align="center">
              <Flex align="center" gap={8}>
                <Avatar
                  icon={<PackageIcon color="#16a34a" size={20} />}
                  shape="square"
                  size={25}
                  style={{ backgroundColor: "#f0fdf4" }}
                />
                <Typography.Title level={5} className={styles.packageCardTitle}>
                  {deliverableData?.deliverableV2?.name
                    ? deliverableData.deliverableV2.name
                    : deliverableData?.deliverableV2?.setNumber
                      ? `Package ${deliverableData.deliverableV2.setNumber}`
                      : null}
                </Typography.Title>
                {defaultDeliverableId && deliverableData?.deliverableV2?._id && defaultDeliverableId === deliverableData?.deliverableV2?._id && (
                  <Tag.CheckableTag
                    checked
                    className={styles.defaultTagChecked}
                    style={{ width: "fit-content" }}
                  >
                    <Flex align="center" gap={4}>
                      <Zap size={10} fill="#f59e0b" />
                      DEFAULT
                    </Flex>
                  </Tag.CheckableTag>
                )}
              </Flex>
              <Flex gap={5}>
                {showMarkDefaultButton &&
                  cardHovered &&
                  defaultDeliverableId !== deliverableData?.deliverableV2?._id && (
                    <Tooltip title="Make this as default">
                      <Button
                        size="small"
                        type="text"
                        icon={<StarFourIcon size={12} />}
                        style={{ padding: 0, height: 25 }}
                        onClick={(event) => {
                          event.stopPropagation();
                          setDefaultDeliverable({
                            deliverableV2Id:
                              deliverableData?.deliverableV2?._id || "",
                          });
                        }}
                        loading={isSetting}
                      />
                    </Tooltip>
                  )}
                {showCopyIcon && cardHovered && copyFn ? (
                  <Button
                    size="small"
                    icon={<Copy size={12} />}
                    style={{ padding: 0, height: 25 }}
                    title="Copy Package"
                    type="text"
                    onClick={(event) => {
                      event.stopPropagation();
                      copyFn();
                    }}
                  />
                ) : null}
                {showEditIcon && cardHovered && editFn ? (
                  <Button
                    size="small"
                    style={{ padding: 0, height: 25 }}
                    type="text"
                    icon={<PenLine size={12} />}
                    onClick={handleEditClick}
                  />
                ) : null}
              </Flex>
            </Flex>
          )
        }
      >
        <Flex vertical gap={8} style={{ height: "100%" }}>
          {/* {deliverableData?.deliverable?.isOldFormat && !hideOldWarning ? (
            <Alert
              style={{ alignItems: "flex-start" }}
              icon={<InfoCircleOutlined style={{ marginTop: 4 }} />}
              showIcon
              message={
                <div>
                  This deliverable was created using an older format.
                  <br />
                  Please ensure that the deliverable is configured correctly.
                </div>
              }
              type="warning"
              action={
                <CampaignDeliverableButton
                  buttonText="Go to Configuration"
                  initialBriefId={briefId}
                />
              }
            />
          ) : null} */}

          {deliverableData ? (
            <DeliverablePillSummary
              deliverable={deliverableData?.deliverableV2}
              showSpecialNotes
            />
          ) : null}

          {/* {deliverableData?.deliverable?.campaignTimelineStart &&
          deliverableData?.deliverable?.campaignTimelineFinish ? (
            <Flex gap={5} align="center">
              <CalendarDotsIcon />
              <Typography.Text className={styles.packageDetailsValue}>
                {`${format(
                  deliverableData.deliverable.campaignTimelineStart,
                  "dd MMM yyyy"
                )} to ${format(
                  deliverableData.deliverable.campaignTimelineFinish,
                  "dd MMM yyyy"
                )}`}
              </Typography.Text>
            </Flex>
          ) : null} */}

          {/* {deliverableData?.deliverable?.isTravelRequired ? (
            <Flex gap={5} align="center">
              <AirplaneTiltIcon width={16} height={16} />
              <Typography.Text ellipsis className={styles.packageDetailsValue}>
                Travel Included: {deliverableData.deliverable.travelDetails}
              </Typography.Text>
            </Flex>
          ) : null} */}


          {hasUsageInfo ? (
            <>
            <Divider style={{ margin: 0 }} className={styles.packageCardDivider} />
            <Flex justify="space-between" align="center" flex={1}>
              <Typography.Text className={styles.assignedToText}>
                Assigned to {countUsed} creators
              </Typography.Text>
              <Tooltip
                placement="bottom"
                title={deliverableData?.usedByBriefInfluencers
                  ?.map((val) => val.socialMedia?.username)
                  .join(", ")}
              >
                <span>
                  <Avatar.Group max={{ count: 5 }} size={15}>
                    {deliverableData?.usedByBriefInfluencers
                      ?.slice(0, 4)
                      ?.map((val) => (
                        <Avatar
                          key={val.socialMedia?._id}
                          style={{
                            textAlign: "center",
                            fontSize: 10,
                            backgroundColor: getColorFromId(
                              val.socialMedia?._id || ""
                            ),
                          }}
                        >
                          {val.socialMedia?.username.charAt(0).toUpperCase()}
                        </Avatar>
                      ))}
                    {countUsed > 4 ? (
                      <Avatar style={{ textAlign: "center" }}>
                        {`+ ${countUsed - 4}`}
                      </Avatar>
                    ) : null}
                  </Avatar.Group>
                </span>
              </Tooltip>
            </Flex>
            </>
          ) : null}
        </Flex>
      </Card>

      {/* {isEditable ? (
        <Flex justify="flex-end">
          <Button
            type="primary"
            disabled={isFetching}
            onClick={() => setOpenPackageList(true)}
          >
            Change Deliverable
          </Button>
        </Flex>
      ) : null} */}

      {/* <Drawer
        open={openPackageList}
        onClose={() => {
          setOpenPackageList(false);
          setSelectedDeliverableForAttach(null);
        }}
        title="Campaign Deliverables"
        width={800}
        footer={
          <Flex
            justify="flex-end"
            style={{
              padding: "16px 24px",
              position: "sticky",
              bottom: 0,
              background: "#fff",
              zIndex: 10,
            }}
          >
            <Button
              type="primary"
              disabled={!selectedDeliverableForAttach}
              title={
                selectedDeliverableForAttach
                  ? ""
                  : "Please select a deliverable."
              }
              loading={isAttaching}
              onClick={() => {
                if (selectedDeliverableForAttach && briefInfluencerIds) {
                  attachBriefDeliverable({
                    deliverableId: selectedDeliverableForAttach._id,
                    briefInfluencerIds,
                  });
                }
              }}
            >
              Attach
            </Button>
          </Flex>
        }
        destroyOnClose
        bodyStyle={{ padding: 0 }}
      >
        <div style={{ width: "100%" }}>
          <CampaignDeliverables
            showTitle={false}
            isEditable={true}
            briefInfluencerIds={briefInfluencerIds}
            selectedDeliverableId={deliverableData?.deliverableV2?._id}
            onClose={() => {
              onClose?.();
              setOpenPackageList(false);
            }}
            onSelectedDeliverableChange={(selectedDeliverable) => {
              setSelectedDeliverableForAttach(
                selectedDeliverable ? { _id: selectedDeliverable._id || "" } : null
              );
            }}
          />
        </div>
      </Drawer> */}

      <Modal
        open={showEditConfirmModal}
        onCancel={() => setShowEditConfirmModal(false)}
        onOk={() => {
          setShowEditConfirmModal(false);
          ToastService.success(
            "This deliverable cannot be edited as it's used by purchased creators. A new package will be created instead."
          );
        }}
        okButtonProps={{ style: { display: "none" } }}
        title="Cannot Edit Deliverable"
        okText="Create New Package"
        cancelText="Close"
      >
        <p>
          This deliverable package is used by{" "}
          {countUsedByBriefInfluencersWithPurchased} purchased creator(s) and
          cannot be edited.
        </p>
        <p>
          Editing this package will create a new package instead. The existing
          package will remain unchanged for the purchased creators.
        </p>
      </Modal>
    </Flex>
  );
}
